import { useCallback, useState } from 'react';
import { Box } from '@chakra-ui/react';

import { QueryTable, TableFilter } from '@app/components/common/table';
import { TransactionInfo, TransactionPackage, TransactionPackageDetail } from '@app/api/models/transaction';
import { useAlert, useApi, useFetchData } from '@app/hooks';
import { TableBase } from '@app/components/common/table/table-base';

import { useColumns } from './columns';
import { TxDrawer } from './components/tx-drawer';
import { SelectTxTypeDialog } from '../dialogs/select-tx-type';
import { CreateTxInstance } from '../dialogs/types';
import { useTransactionColumns } from './transactions-columns';

export const TransactionsTable = () => {
  const [openCreateDialog, setOpenCreateDialog] = useState<'correction' | 'moving' | 'none'>('none');
  const [packageDetails, setPackageDetails] = useState<Record<string, TransactionPackageDetail & { isCollapsed: boolean }>>({});

  const [selectedTx, setSelectedTx] = useState<TransactionPackage | TransactionInfo>();
  const { transaction } = useApi();
  const { success } = useAlert();
  const [filters, setFilters] = useState<Record<string, string | number | boolean>>({});

  const [fetchData] = useFetchData<TransactionPackage>({ method: transaction.getTransactionPackages });

  const onShowPackageDetail = useCallback(async (packageId: string) => {
    if (packageDetails[packageId]) {
      setPackageDetails((packageDetails) => ({
        ...packageDetails,
        [packageId]: {
          ...packageDetails[packageId],
          isCollapsed: !packageDetails[packageId].isCollapsed,
        }
      }));
      return;
    }
    setPackageDetails((packageDetails) => ({
      ...packageDetails,
      [packageId]: {
        ...packageDetails[packageId],
        isCollapsed: false,
      }
    }));

    const { data: packageData } = await transaction.getTransactionPackage({ 
      package_id: packageId,
      ...filters,
    }); 

    setPackageDetails((packageDetails) => ({
      ...packageDetails,
      [packageId]: {
        id: packageId,
        ...packageData,
        transactions: packageData.transactions.map((tx) => ({
          ...tx,
          user: packageData.user,
          outlet_from: packageData.outlet_from,
          outlet_to: packageData.outlet_to,
          distributor: packageData.distributor,
        })),
        isCollapsed: false,
      }
    }));
    
  },[packageDetails, filters]);

  const columns = useColumns({ packageDetails, onShowPackageDetail } );

  const onClose = useCallback(() => {
    setSelectedTx(undefined);
  }, []);

  const onCreateTx = async (tx: CreateTxInstance) => {
    const  response = await transaction.create({
      type_id: Number(tx.type.id),
      equips: tx.equip ? [Number(tx.equip.id)] : tx.equips.map(({ id }) => Number(id)),
      outlet_to_id: tx.to.id,
      distributor_id: Number(tx.distr.id),
      comment: tx.comment
    });

    if (response.code === 200) {
      success('Транзакция создана успешно');
      setOpenCreateDialog('none');
    }
  };

  const transactionColumns = useTransactionColumns();

  const onFilterChange = useCallback((filter: TableFilter<TransactionPackage>[]) => {
    setPackageDetails({});
    setFilters(filter.filter(({ value }) => !!value ).reduce((acc, curr) => ({ ...acc, [curr.name]: curr.value }), {}));
  }, []);

  return <>
    <QueryTable<TransactionPackage>
      columns={columns}
      title="Транзакции оборудования" 
      initialSort={{ name: 'create_dt', direction: 'desc' }}
      fetchData={fetchData}
      onFilterChange={onFilterChange}
      onRowClick={(tr: TransactionPackage) => setSelectedTx(tr)}
      createMenu={[ { id: 'correction', title: 'Корректировка' }, { id: 'moving', title: 'Перемещение'} ]}
      onCreateClick={(item) => setOpenCreateDialog(item.id as ('correction' | 'moving'))}
      renderGroup={(row) => {
        if (!packageDetails[row.id] || packageDetails[row.id]?.isCollapsed) {
          return null;
        }
        return <Box
          backgroundColor="blue.100"
          pl={12}
        >
          <TableBase<TransactionInfo> title=""
            loading={!packageDetails[row.id]?.transactions?.length} 
            columns={transactionColumns} 
            data={packageDetails[row.id].transactions || []}  
            onRowClick={(tr: TransactionInfo) => setSelectedTx(tr)}
            paper={false}      
          />
        </Box>;
      }}
    >
      <SelectTxTypeDialog 
        dialog={openCreateDialog}
        onCancel={() => setOpenCreateDialog('none')}
        onSubmit={onCreateTx}
      />
      <TxDrawer 
        selectedTx={selectedTx}
        onClose={onClose}
      />
    </QueryTable>
  </>;
};

